import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DropdownList } from '../DropdownList';
import './styles.scss';

function useClickOutside(ref, callback) {
  useEffect(() => {
    function handleClickOutside(event) {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [ref, callback]);
}

const Dropdown = ({
  defaultText,
  value,
  onChange,
  onBlur,
  renderOption,
  renderSelected,
  options,
  name,
  label,
  error,
}) => {
  const wrapperRef = useRef(null);
  const [showOptionList, setShowOptionList] = useState(false);
  const [selected, setSelected] = useState('');
  const selectedOption = options.find(o => o.value === selected);
  function handleListDisplay() {
    setShowOptionList(v => !v);
  }
  function handleOptionClick(option) {
    if (!option.readOnly) {
      setSelected(option.value);
    }
    setShowOptionList(false);
    onChange(
      { target: { name, value: option.value } },
      { name, value: option.value }
    );
  }
  useEffect(() => {
    setSelected(value);
  }, [value]);
  useClickOutside(wrapperRef, () => {
    handleListDisplay();
    onBlur({ target: { name, value: selected } }, { name, value: selected });
  });
  return (
    <div className="form-group">
      <div className="select">
        <label className="form-label" htmlFor={name}>
          {label}
        </label>
        <div
          className="form-select select__selected"
          onClick={handleListDisplay}
        >
          {selectedOption ? renderSelected(selectedOption) : defaultText}
        </div>
        {showOptionList && (
          <div className="select__options" ref={wrapperRef}>
            <DropdownList
              selected={selected}
              options={options}
              onClick={handleOptionClick}
              renderOption={renderOption}
            />
          </div>
        )}
        {error.length > 0 && <div className="text-error">{error}</div>}
      </div>
    </div>
  );
};

Dropdown.propTypes = {
  /** The text displayed in the dropdown */
  defaultText: PropTypes.string,
  value: PropTypes.oneOf([PropTypes.string, PropTypes.number]),
  /** Called when the user attempts to change the value. */
  onChange: PropTypes.func,
  /** Called on blur. */
  onBlur: PropTypes.func,
  /** Mapped over the items and returns element to render in the list */
  renderOption: PropTypes.func,
  /** Returns selected element to render */
  renderSelected: PropTypes.func,
  /** Array of list items */
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    data: PropTypes.shape(),
  })),
  /** Currently selected item */
  name: PropTypes.string,
  label: PropTypes.string,
  error: PropTypes.string,
};

Dropdown.defaultProps = {
  defaultText: '',
  value: '',
  onChange: () => {},
  onBlur: () => {},
  renderOption: o => o.title,
  renderSelected: o => o.title,
  name: '',
  label: '',
  error: '',
  options: [],
};

export { Dropdown };
